import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { PagePath, Button, LinkButton, Form, DataTable, BulkUploadTemplate, IconCSV } from "../../components";
import { taskActions, getBulkUploadsHistory, getBulkUploadStatus, getCurrentUser } from "../../store";
import Fields from "./FormFields.json";
import { useNavigate } from "react-router-dom";

const Columns = [
  { title: 'Filename', dataIndex: 'document_file_name', key: 'document_file_name', width: '90%', search: true, sort: true, classNames: { cell: '' } },
  { title: 'Upload Date', dataIndex: 'created_at', key: 'created_at', width: '180px', sort: true },
  { title: 'Task Created', dataIndex: 'created', key: 'created', width: '120px', classNames: { cell: 'txt-right bold-500' } },
  { title: 'Task Creation Failed', dataIndex: 'failed', key: 'failed', width: '175px', classNames: { cell: 'txt-right bold-500' } },
  { title: 'Download', dataIndex: 'frequency', key: 'frequency', width: '100px', render: 'btn', classNames: { cell: 'self-ctr' } },
]

const Steps = [
  "Download the Template provided below.",
  "Prepare existing Tasks as per the Template.",
  "Import prepared Tasks when ready!"
]

export const BulkUploadScreen = () => {
  const _ref = React.useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [uploadStatus, setUploadStatus] = React.useState(false);
  const status = useSelector(getBulkUploadStatus)
  const history = useSelector(getBulkUploadsHistory)
  const user = useSelector(getCurrentUser);
  const allowedRoles = [
    'Assessor',
    'Architect',
    'Organiser',
    'Governor',
    'Administrator'
  ];
  const [height, setHeight] = React.useState(0)
  React.useEffect(() => {
    dispatch(taskActions.fetchBulkUploads())
  }, [])
  // Redirect unauthorized users
  React.useEffect(() => {
    if (user && !allowedRoles.includes(user.role.name)) {
      navigate('/access-restricted'); // Redirect to Access Restricted if unauthorized
    }
  }, [user, navigate]);
  React.useEffect(() => {
    if (!height && _ref.current) {
      setHeight(_ref.current.clientHeight)
    }
  }, [_ref])
  React.useEffect(() => {
    if (status) {
      setUploadStatus(status);
      dispatch(taskActions.setBulkUploadStatus(false))
    }
  }, [status])
  const handleDownloadHistory = (e, upload) => {
    if (upload && upload.error_file_path) {
      dispatch(taskActions.downloadBulkUploadHistory(upload))
    }
  }
  const getColumns = () => {
    let columns = Columns.map((_) => ({ ..._ }));
    columns[columns.length - 1] = {
      ...columns[columns.length - 1],
      cellClassName: 'download-cell',
      onClick: handleDownloadHistory,
      icon: <DownloadOutlined style={{ fontSize: 20, color: '#1133CC' }} />
    }
    return columns;
  }
  const handleBulkUpload = (e) => {
    let files = e.target.value;
    if (Array.isArray(files) && files.length > 0) {
      files = files.map((_) => _.originFileObj);
      const payload = new FormData();
      payload.append('file', files[0])
      dispatch(taskActions.bulkUploadTasks(payload))
    }
  }
  const fileName = uploadStatus?.document_file_name || 'Unknown_File';
  const fileParts = fileName.split('.');
  const fileNameWithResult = `${fileParts[0]}_Result.${fileParts[1]}`;

  return (
    <div className="col w-100 h-100 o-hide bgFFF upload-task screen-pad">
      <div className="col w-100 header">
        <div className="row w-100 item ">
          <PagePath
            paths={[{ title: 'Tracker', to: '/' }, { title: 'Custom Tasks' }]}
          />
        </div>
        <div className="row exo2 f20 bold line line-17 c238787 item">
          Custom Tasks
        </div>
      </div>

      <div className="col w-100 f-rest o-hide">
        <div className="col w-100 h-100 0-hide">
          <div className="row w-100 v-start">
            <div className="col f-rest h-btn h-100">
              <div className="col w-100 ">
                {
                  Steps.map((step, i) => {
                    return (
                      <div className="row f16 00085" key={i}>
                        <span className="bold step">Step {i + 1}:</span> {step}
                      </div>
                    )
                  })
                }
              </div>
              <div className="row w-100 template-download">
                <IconCSV className="f30 c000" />
                <div className="row f-rest">
                  <span className="f14 c1133CC">Tasks_Upload_Template.xlsx</span>
                </div>
                <Button
                  type="link"
                  href={BulkUploadTemplate}
                  download="Bulk Upload Template.xlsx" target="_blank"
                  icon={<DownloadOutlined style={{ fontSize: 20, color: '#1133CC' }} />}
                />
              </div>
            </div>
            <Form
              className="upload-sec"
              formData={{ upload: [] }}
              onChange={handleBulkUpload}
              Fields={[Fields.BulkUpload]}
            />
          </div>
          <div className="col w-100 f-rest 0-hide">
            <div className="row w-100 h-100 0-hide result-sec v-start">
              {
                Boolean(uploadStatus) &&
                <div className="col upload-result">
                  <div className="row header bgFAFAFA">
                    <span className="exo2 f18 bold-600 c00085">Upload Result</span>
                  </div>
                  <div className="col w-100 f16 result">
                    {
                      uploadStatus === 'started' ?
                        <div className="col w-100 h-100 v-ctr h-ctr result-loading">
                          <LoadingOutlined style={{ color: "#0133CC", fontSize: 30 }} />
                        </div>
                        :
                        <p>
                          <b>{fileName}</b> was successfully uploaded with the following results…<br /> <br />
                          Tasks created: <span className="bold">{uploadStatus.created}</span> <br />
                          Tasks creation <span className="cF00">failed</span>: <span className="bold">{uploadStatus.failed}</span><br /> <br />
                          Task upload {uploadStatus.failed > 0 ? "errors" : "report"}: <LinkButton onClick={handleDownloadHistory.bind(null, null, uploadStatus)} variant='lite' className="c1133CC" label={fileNameWithResult} /> <br /><br />
                          {uploadStatus.failed > 0 && <span>Fix the errors & re-upload them please!</span>}
                        </p>
                    }
                  </div>
                </div>
              }
              <div className="col f-rest h-100 o-hide">
                <div className="row header bgFAFAFA">
                  <span className="exo2 f18 bold-600 c00085">Upload History</span>
                </div>
                <div className="col f-rest o-hide" ref={_ref}>
                  <div className="col w-100 h-100 o-hide" style={{ height }}>
                    <DataTable
                      noSelection
                      emptyMessage="There are no History right now!"
                      Columns={getColumns()}
                      rows={history}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}